import { Link } from "react-router-dom";
import { Check, ArrowRight, Camera, Film, Briefcase } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { IMAGES } from "@/data";

const PACKAGES = [
  {
    id: "compleanni",
    name: "Compleanni & Cerimonie",
    tagline: "Servizio rapido e dinamico",
    icon: Camera,
    highlight: false,
    features: [
      "Copertura fotografica completa dell'evento (fino al taglio della torta)",
      "Sessione di ritrattistica dedicata",
      "Post-produzione professionale degli scatti migliori (circa 180-200)",
      "VANTAGGIO ESCLUSIVO: consegna garantita in digitale entro soli 10 giorni",
    ],
    cta: "Richiedi Preventivo",
    ctaTestId: "package-compleanni-cta",
  },
  {
    id: "wedding",
    name: "Wedding Premium",
    tagline: "Foto e Video · Reportage completo",
    icon: Film,
    highlight: true,
    features: [
      "Reportage fotografico completo a partire dalla vestizione a casa",
      "Copertura totale foto e video della cerimonia e del ricevimento",
      "Montaggio video professionale (Wedding Film) e post-produzione di alta qualità",
      "VANTAGGIO 1: anticipo foto (Sneak Peek) consegnato entro 14 giorni",
      "VANTAGGIO 2: consegna del lavoro completo in digitale entro 60 giorni",
      "Album sposi di altissima qualità con 60 fotografie selezionate",
    ],
    cta: "Verifica la disponibilità della data",
    ctaTestId: "package-wedding-cta",
  },
];

const Servizi = () => (
  <div data-testid="servizi-page" className="bg-[#0c0c0c] pt-32">
    <Seo
      title="Servizi & Pacchetti Fotografici | Matrimoni, Eventi e Aziende · Tuscia Viterbese"
      description="Pacchetti fotografici su misura: Compleanni & Cerimonie, Wedding Premium foto e video, Foto & Video Aziendali. Servizi fotografici nella Tuscia Viterbese e a Roma."
      path="/servizi"
    />
    {/* Header */}
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-12">
      <Reveal>
        <span className="text-sm uppercase tracking-[0.3em] text-gold">Servizi & Pacchetti</span>
        <h1 className="font-serif text-4xl md:text-6xl text-white font-light mt-6 max-w-3xl leading-tight">
          Pacchetti fotografici su misura
        </h1>
        <p className="text-stone-400 mt-6 max-w-2xl leading-relaxed">
          Servizi fotografici per eventi nella Tuscia Viterbese e a Roma. Reportage emozionale per matrimoni,
          copertura rapida per compleanni e cerimonie. Scegli il pacchetto più adatto a te.
        </p>
      </Reveal>
      <Reveal delay={150} className="mt-10">
        <div className="overflow-hidden">
          <img src={IMAGES.servicesBanner} alt="Reportage emozionale di matrimonio nella Tuscia Viterbese" className="w-full h-[300px] md:h-[440px] object-cover hover:scale-105 transition-transform duration-700" />
        </div>
      </Reveal>
    </section>

    {/* Pricing cards */}
    <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {PACKAGES.map((pkg, i) => (
          <Reveal
            key={pkg.id}
            delay={i * 150}
            data-testid={`package-${pkg.id}`}
            className={`relative flex flex-col p-9 bg-[#111] border ${
              pkg.highlight ? "border-gold" : "border-stone-800"
            }`}
          >
            {pkg.highlight && (
              <span className="absolute -top-3 left-9 bg-gold text-[#0c0c0c] text-[10px] uppercase tracking-[0.25em] px-3 py-1 font-semibold">
                Più richiesto
              </span>
            )}
            <pkg.icon className="text-gold" size={30} />
            <h2 className="font-serif text-2xl md:text-3xl text-white mt-5">{pkg.name}</h2>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-2">{pkg.tagline}</p>

            <ul className="space-y-4 mt-8 flex-1">
              {pkg.features.map((f, idx) => (
                <li key={idx} className="flex gap-3 text-sm text-stone-300 leading-relaxed">
                  <Check size={18} className="text-gold shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/contatti"
              data-testid={pkg.ctaTestId}
              className={`mt-9 flex items-center justify-center gap-2 uppercase tracking-[0.15em] text-sm font-medium py-4 transition-colors duration-300 ${
                pkg.highlight
                  ? "bg-gold text-[#0c0c0c] hover:bg-[#b5952f]"
                  : "border border-stone-600 text-white hover:border-gold hover:text-gold"
              }`}
            >
              {pkg.cta} <ArrowRight size={16} />
            </Link>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Foto Aziendali */}
    <section className="relative py-24 bg-[#0a0a0a]" data-testid="servizi-aziende">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="overflow-hidden">
            <img src={IMAGES.corporate} alt="Servizi fotografici eventi aziendali nella Tuscia Viterbese e a Roma" className="w-full h-[440px] object-cover hover:scale-105 transition-transform duration-700" />
          </div>
        </Reveal>
        <Reveal delay={150}>
          <div className="flex items-center gap-3 mb-3">
            <Briefcase className="text-gold" size={24} />
            <span className="text-xs uppercase tracking-[0.25em] text-gold">Business</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-white font-light">Foto & Video Aziendali</h2>
          <p className="text-stone-400 mt-5 leading-relaxed">
            Servizi fotografici e video professionali per aziende nella Tuscia Viterbese e a Roma.
            Immagini che valorizzano il tuo brand, i tuoi eventi e la tua squadra.
          </p>
          <ul className="space-y-3 mt-6">
            {[
              "Copertura di eventi e convention aziendali",
              "Ritratti corporate e foto del team",
              "Foto di prodotto e degli ambienti di lavoro",
              "Contenuti ottimizzati per social e sito web",
              "Servizio e post-produzione concordati su misura in base alle esigenze del brand",
            ].map((f, i) => (
              <li key={i} className="flex gap-3 text-sm text-stone-300 leading-relaxed">
                <Check size={17} className="text-gold shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/contatti"
            data-testid="servizi-aziende-cta"
            className="inline-flex items-center gap-2 bg-gold text-[#0c0c0c] uppercase tracking-[0.15em] text-sm font-medium px-8 py-4 mt-8 hover:bg-[#b5952f] transition-colors duration-300"
          >
            Richiedi Preventivo <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Servizi;
